import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { FlowHost } from "~/api/client";
import { useFlowDefinition, useIdeSessions, useRunFlow } from "~/api/queries";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { AreaField, Note, SelectField, TextField } from "./FormBits";

/**
 * Start a flow (W3 §A.4). One dialog, used by both the manager and the studio — the fields are
 * the same, and two copies would drift the moment one gained the token budget.
 *
 * The prompt is prefilled from the definition's `defaultPrompt`, fetched lazily: that value is
 * what a scheduled run of this flow would use, so showing it is the difference between "what
 * will this do" and "what would I like it to do".
 */
export function RunFlowDialog({
  slug,
  name,
  onClose,
}: {
  slug: string;
  name: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState("");
  const [host, setHost] = useState<FlowHost>("auto");
  const [target, setTarget] = useState("");
  const [budget, setBudget] = useState("");
  const [prefilled, setPrefilled] = useState(false);

  const runFlow = useRunFlow();
  const { data: definition } = useFlowDefinition(slug);
  const { data: sessions } = useIdeSessions();

  // Prefill ONCE. Refetching the definition mid-sentence must not retype the author's prompt.
  useEffect(() => {
    if (prefilled || !definition) {
      return;
    }
    const fallback = (definition.definition as { defaultPrompt?: unknown } | null)?.defaultPrompt;
    if (typeof fallback === "string" && fallback !== "") {
      setPrompt(fallback);
    }
    setPrefilled(true);
  }, [definition, prefilled]);

  const online = (sessions ?? []).filter((session) => session.isOnline);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const tokenBudget = Number(budget.trim());
    runFlow.mutate(
      {
        slug,
        request: {
          prompt,
          host,
          ...(target ? { targetSessionKey: target } : {}),
          ...(Number.isFinite(tokenBudget) && tokenBudget > 0 ? { tokenBudget } : {}),
        },
      },
      {
        onSuccess: (ack) => {
          onClose();
          // The server's own note is the honest answer — it says whether anything picked the
          // work up. Inventing "Started" is how a queued-and-expired run reads as a success.
          toast.success(ack.note || t("The flow was handed off"));
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Run “{{name}}”", { name })}
      closeLabel={t("Close")}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button variant="primary" disabled={runFlow.isPending} onClick={submit}>
            {t("Run")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <AreaField
          label={t("Prompt")}
          value={prompt}
          onChange={setPrompt}
          placeholder={t("Optional. What should this run pay attention to?")}
          hint={t("Prefilled from the flow's default prompt — the one a scheduled run would use.")}
        />

        <SelectField
          label={t("Where it runs")}
          value={host}
          onChange={(value) => setHost(value as FlowHost)}
          options={[
            { value: "auto", label: t("Automatic") },
            { value: "server", label: t("On the server") },
            { value: "ide", label: t("In an editor window") },
          ]}
          hint={t(
            "A flow that needs an editor waits for a window to claim it, and expires if none does.",
          )}
        />

        <SelectField
          label={t("Target window")}
          value={target}
          onChange={setTarget}
          options={[
            { value: "", label: t("Any window that is online") },
            ...online.map((session) => ({
              value: session.sessionKey,
              label: session.workspaceName ?? session.machineName ?? session.sessionKey,
            })),
          ]}
          hint={
            online.length === 0
              ? t("No editor window is connected right now.")
              : t("Aim the dispatch at one window instead of whichever claims it first.")
          }
        />

        <TextField
          label={t("Token budget")}
          type="number"
          value={budget}
          onChange={setBudget}
          placeholder={t("Optional")}
          hint={t("A ceiling for this run only. Blank leaves the flow's own policy in charge.")}
        />

        {host === "server" ? (
          <Note tone="warn">
            {t(
              "The server host refuses nodes it cannot execute — anything owned by an editor, and the forEach, subflow and wait kinds.",
            )}
          </Note>
        ) : null}
      </form>
    </Dialog>
  );
}
